import {
  useOrganisation,
  usePermissions,
  useUpdateOrganisationTmsShippingIntegration,
} from '@my/directory-api/hooks';
import type { FerryCredential } from '@my/ferry-api';
import {
  useCreateFerryCredential,
  useDeleteFerryCredential,
  useFerryCarriers,
  useFerryCredentials,
  useTestFerryCredential,
  useUpdateFerryCredentialStatus,
} from '@my/ferry-api/hooks';
import {
  Button,
  ConfirmDialog,
  ErrorBanner,
  FormSection,
  Loading,
  SelectInput,
  Switch,
  TextInput,
  showToast,
} from '@my/ui';
import { Check, Pause, Play, PlugZap, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  parseShippingIntegration,
  serialiseShippingIntegration,
  type ShippingIntegration,
} from '../../ferry/shippingIntegration';

export interface ShippingIntegrationSectionProps {
  /** Owning organisation id; the card stays disabled while this is null. */
  organisationId: string | null;
}

interface AccountDraft {
  carrierId: string;
  customerCode: string;
  clientId: string;
  clientSecret: string;
  resource: string;
}

const EMPTY_ACCOUNT: AccountDraft = {
  carrierId: '',
  customerCode: '',
  clientId: '',
  clientSecret: '',
  resource: '',
};

/**
 * Whether this organisation books ferry crossings through the ferry service,
 * and the carrier accounts it books under.
 *
 * <p>
 * STRATUM SUPPORT ONLY, and it asks here, exactly as `CopyFunctionsSection`
 * does: the Directory endpoint answers 404 to a customer token, so this is
 * the affordance rather than the defence. The accounts table follows the
 * Subsistence GPS integration section: Add opens an inline row, the secret
 * is sent once and never shown again, and there is no edit.
 * </p>
 */
export function ShippingIntegrationSection({ organisationId }: ShippingIntegrationSectionProps) {
  const { t } = useTranslation(['configuration', 'common']);
  const { role } = usePermissions();
  const isSupport = role === 'SuperUser';

  // ── The switch, held by Directory ──
  const organisationQuery = useOrganisation(organisationId);
  const saveMutation = useUpdateOrganisationTmsShippingIntegration();
  const stored = useMemo(
    () => parseShippingIntegration(organisationQuery.data?.tmsShippingIntegrationJson),
    [organisationQuery.data?.tmsShippingIntegrationJson],
  );
  const [draft, setDraft] = useState<ShippingIntegration>(stored);
  const storedKey = serialiseShippingIntegration(stored);
  useEffect(() => {
    setDraft(parseShippingIntegration(storedKey));
  }, [storedKey]);
  const isDirty = serialiseShippingIntegration(draft) !== storedKey;

  // ── The accounts, held by the ferry service ──
  const carriersQuery = useFerryCarriers({ enabled: isSupport });
  const credentialsQuery = useFerryCredentials(isSupport ? organisationId : null);
  const createMutation = useCreateFerryCredential();
  const statusMutation = useUpdateFerryCredentialStatus();
  const deleteMutation = useDeleteFerryCredential();
  const testMutation = useTestFerryCredential();
  const [adding, setAdding] = useState(false);
  const [account, setAccount] = useState<AccountDraft>(EMPTY_ACCOUNT);
  const [pendingDelete, setPendingDelete] = useState<FerryCredential | null>(null);

  // An organisation may hold more than one account at a carrier, so every
  // active carrier stays on offer.
  const carrierOptions = useMemo(
    () =>
      (carriersQuery.data ?? [])
        .filter((c) => c.status === 'Active')
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        .map((c) => ({ value: c.id, label: c.name })),
    [carriersQuery.data],
  );
  const carrierName = (id: string) => carriersQuery.data?.find((c) => c.id === id)?.name ?? id;
  const accounts = credentialsQuery.data ?? [];
  const accountsReady = credentialsQuery.isSuccess && carriersQuery.isSuccess;
  const busy =
    createMutation.isPending ||
    statusMutation.isPending ||
    deleteMutation.isPending ||
    testMutation.isPending;

  if (!isSupport) return null;

  const handleSave = async () => {
    if (!organisationId) return;
    try {
      await saveMutation.mutateAsync({
        organisationId,
        settingsJson: serialiseShippingIntegration(draft),
      });
      showToast({ type: 'success', message: t('configuration:shippingIntegration.saved') });
    } catch (err) {
      showToast({
        type: 'error',
        message:
          err instanceof Error ? err.message : t('configuration:shippingIntegration.saveFailed'),
      });
    }
  };

  const startAdding = () => {
    setAccount(EMPTY_ACCOUNT);
    setAdding(true);
  };

  const cancelAdding = () => {
    if (busy) return;
    setAdding(false);
    setAccount(EMPTY_ACCOUNT);
  };

  const handleAdd = async () => {
    if (!organisationId || busy) return;
    try {
      await createMutation.mutateAsync({
        organisationId,
        carrierId: account.carrierId,
        customerCode: account.customerCode.trim(),
        clientId: account.clientId.trim(),
        clientSecret: account.clientSecret,
        resource: account.resource.trim() || null,
      });
      setAdding(false);
      setAccount(EMPTY_ACCOUNT);
      showToast({
        type: 'success',
        message: t('configuration:shippingIntegration.accounts.added'),
      });
    } catch (err) {
      showToast({
        type: 'error',
        message:
          err instanceof Error
            ? err.message
            : t('configuration:shippingIntegration.accounts.addFailed'),
      });
    }
  };

  const handleTest = async (credential: FerryCredential) => {
    try {
      const outcome = await testMutation.mutateAsync(credential.id);
      showToast({
        type: outcome.succeeded ? 'success' : 'error',
        message:
          outcome.message ||
          t(
            outcome.succeeded
              ? 'configuration:shippingIntegration.accounts.testOk'
              : 'configuration:shippingIntegration.accounts.testFailed',
          ),
      });
    } catch (err) {
      showToast({
        type: 'error',
        message:
          err instanceof Error
            ? err.message
            : t('configuration:shippingIntegration.accounts.testFailed'),
      });
    }
  };

  const handleStatus = async (credential: FerryCredential) => {
    if (!organisationId) return;
    try {
      await statusMutation.mutateAsync({
        id: credential.id,
        organisationId,
        status: credential.status === 'Active' ? 'Inactive' : 'Active',
      });
    } catch (err) {
      showToast({
        type: 'error',
        message:
          err instanceof Error
            ? err.message
            : t('configuration:shippingIntegration.accounts.statusFailed'),
      });
    }
  };

  const confirmDelete = async () => {
    if (!organisationId || !pendingDelete) return;
    try {
      await deleteMutation.mutateAsync({ id: pendingDelete.id, organisationId });
      showToast({
        type: 'success',
        message: t('configuration:shippingIntegration.accounts.deleted'),
      });
    } catch (err) {
      showToast({
        type: 'error',
        message:
          err instanceof Error
            ? err.message
            : t('configuration:shippingIntegration.accounts.deleteFailed'),
      });
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <FormSection
      title={t('configuration:shippingIntegration.title')}
      columns={3}
      disabled={!organisationId}
      footer={
        <Button
          type="button"
          disabled={!isDirty || !organisationId}
          loading={saveMutation.isPending}
          onClick={handleSave}
        >
          {t('common:actions.save')}
        </Button>
      }
    >
      <p className="text-sm text-slate-500 md:col-span-3">
        {t('configuration:shippingIntegration.description')}
      </p>

      {organisationQuery.isPending ? (
        <div className="md:col-span-3">
          <Loading label={t('common:states.loading')} />
        </div>
      ) : organisationQuery.isError ? (
        <div className="md:col-span-3">
          <ErrorBanner
            title={t('configuration:shippingIntegration.loadFailed')}
            message={organisationQuery.error.message}
          />
        </div>
      ) : (
        <Switch
          name="shippingIntegration-enabled"
          label={t('configuration:shippingIntegration.enabled')}
          checked={draft.enabled}
          enabledLabel={t('configuration:shippingIntegration.on')}
          disabledLabel={t('configuration:shippingIntegration.off')}
          onChange={(e) => setDraft({ enabled: e.target.checked })}
        />
      )}

      {/* The organisation's accounts at each carrier, straight from the ferry service. */}
      <div className="flex items-start justify-between gap-3 md:col-span-3">
        <div>
          <h4 className="text-sm font-medium text-slate-800">
            {t('configuration:shippingIntegration.accounts.title')}
          </h4>
          <p className="text-xs text-slate-500">
            {t('configuration:shippingIntegration.accounts.description')}
          </p>
        </div>
        {!adding && accountsReady && (
          <button
            type="button"
            onClick={startAdding}
            disabled={!organisationId || busy}
            className="flex shrink-0 items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('configuration:shippingIntegration.accounts.add')}
          </button>
        )}
      </div>

      {credentialsQuery.isPending || carriersQuery.isPending ? (
        <div className="md:col-span-3">
          <Loading label={t('common:states.loading')} />
        </div>
      ) : !accountsReady ? (
        <div className="md:col-span-3">
          <ErrorBanner
            title={t('configuration:shippingIntegration.accounts.title')}
            message={t('configuration:shippingIntegration.accounts.ferryUnavailable')}
          />
        </div>
      ) : (
        <div className="overflow-x-auto md:col-span-3">
          <div className="min-w-[480px] overflow-hidden rounded-md">
            <table className="w-full table-auto text-xs">
              <thead className="bg-slate-700 text-[11px] uppercase tracking-wide text-white">
                <tr>
                  <Th>{t('configuration:shippingIntegration.accounts.carrier')}</Th>
                  <Th>{t('configuration:shippingIntegration.accounts.customerCode')}</Th>
                  <Th>{t('configuration:shippingIntegration.accounts.status')}</Th>
                  <Th align="right">{t('configuration:shippingIntegration.accounts.actions')}</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-500 bg-white">
                {adding && (
                  <tr>
                    <td colSpan={4} className="bg-slate-50 px-3 py-3">
                      <AccountEditor
                        draft={account}
                        options={carrierOptions}
                        busy={busy}
                        onChange={setAccount}
                        onSave={() => void handleAdd()}
                        onCancel={cancelAdding}
                      />
                    </td>
                  </tr>
                )}

                {accounts.map((credential) => (
                  <AccountRow
                    key={credential.id}
                    credential={credential}
                    carrierName={carrierName(credential.carrierId)}
                    locked={adding || busy}
                    onTest={() => void handleTest(credential)}
                    onToggleStatus={() => void handleStatus(credential)}
                    onDelete={() => setPendingDelete(credential)}
                  />
                ))}

                {accounts.length === 0 && !adding && (
                  <tr>
                    <td colSpan={4} className="px-3 py-4 text-center text-xs italic text-slate-400">
                      {t('configuration:shippingIntegration.accounts.empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title={t('configuration:shippingIntegration.accounts.confirmDeleteTitle')}
        message={t('configuration:shippingIntegration.accounts.confirmDeleteMessage')}
        confirmLabel={t('configuration:shippingIntegration.accounts.delete')}
        busy={deleteMutation.isPending}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          if (!deleteMutation.isPending) setPendingDelete(null);
        }}
      />
    </FormSection>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th
      className={`whitespace-nowrap px-3 py-2 font-semibold ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}

function AccountRow({
  credential,
  carrierName,
  locked,
  onTest,
  onToggleStatus,
  onDelete,
}: {
  credential: FerryCredential;
  carrierName: string;
  locked: boolean;
  onTest: () => void;
  onToggleStatus: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation('configuration');
  const isActive = credential.status === 'Active';
  const testLabel = t('shippingIntegration.accounts.test');
  const statusLabel = t(
    isActive ? 'shippingIntegration.accounts.disable' : 'shippingIntegration.accounts.enable',
  );
  const deleteLabel = t('shippingIntegration.accounts.delete');

  return (
    <tr className={locked ? 'opacity-50' : undefined}>
      <td className="whitespace-nowrap px-3 py-0.5 align-middle font-semibold text-slate-900">
        {carrierName}
      </td>
      <td className="whitespace-nowrap px-3 py-0.5 align-middle tabular-nums text-slate-700">
        {credential.customerCode}
      </td>
      <td className="whitespace-nowrap px-3 py-0.5 align-middle">
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${
            isActive
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-slate-200 bg-slate-50 text-slate-500'
          }`}
        >
          {t(
            isActive
              ? 'shippingIntegration.accounts.active'
              : 'shippingIntegration.accounts.inactive',
          )}
        </span>
      </td>
      <td className="whitespace-nowrap px-3 py-0.5 text-right align-middle">
        <div className="inline-flex items-center gap-1">
          <button
            type="button"
            aria-label={testLabel}
            title={testLabel}
            onClick={onTest}
            disabled={locked}
            className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100 disabled:opacity-50"
          >
            <PlugZap className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={statusLabel}
            title={statusLabel}
            onClick={onToggleStatus}
            disabled={locked}
            className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100 disabled:opacity-50"
          >
            {isActive ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          </button>
          <button
            type="button"
            aria-label={deleteLabel}
            title={deleteLabel}
            onClick={onDelete}
            disabled={locked}
            className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function AccountEditor({
  draft,
  options,
  busy,
  onChange,
  onSave,
  onCancel,
}: {
  draft: AccountDraft;
  options: { value: string; label: string }[];
  busy: boolean;
  onChange: (next: AccountDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('configuration');
  const setField = <K extends keyof AccountDraft>(key: K, value: AccountDraft[K]) =>
    onChange({ ...draft, [key]: value });

  const isValid =
    !!draft.carrierId &&
    !!draft.customerCode.trim() &&
    !!draft.clientId.trim() &&
    !!draft.clientSecret;
  const cancelLabel = t('shippingIntegration.accounts.cancel');
  const saveLabel = t('shippingIntegration.accounts.save');

  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        <SelectInput
          label={t('shippingIntegration.accounts.carrier')}
          name="shippingIntegration-carrier"
          value={draft.carrierId}
          options={options}
          placeholder={t('shippingIntegration.accounts.pickCarrier')}
          disabled={busy || options.length === 0}
          onChange={(e) => setField('carrierId', e.target.value)}
        />
        <TextInput
          label={t('shippingIntegration.accounts.customerCode')}
          name="shippingIntegration-customer-code"
          autoComplete="off"
          value={draft.customerCode}
          disabled={busy}
          maxLength={64}
          onChange={(e) => setField('customerCode', e.target.value)}
        />
        <TextInput
          label={t('shippingIntegration.accounts.clientId')}
          name="shippingIntegration-client-id"
          autoComplete="off"
          value={draft.clientId}
          disabled={busy}
          maxLength={256}
          onChange={(e) => setField('clientId', e.target.value)}
        />
        <TextInput
          label={t('shippingIntegration.accounts.clientSecret')}
          name="shippingIntegration-client-secret"
          type="password"
          autoComplete="new-password"
          value={draft.clientSecret}
          disabled={busy}
          maxLength={512}
          onChange={(e) => setField('clientSecret', e.target.value)}
        />
        <TextInput
          label={t('shippingIntegration.accounts.resource')}
          name="shippingIntegration-resource"
          autoComplete="off"
          value={draft.resource}
          disabled={busy}
          maxLength={512}
          onChange={(e) => setField('resource', e.target.value)}
        />
      </div>

      <div className="flex items-center justify-end gap-1 pt-1">
        <button
          type="button"
          aria-label={cancelLabel}
          title={cancelLabel}
          onClick={onCancel}
          disabled={busy}
          className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
        >
          <X className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label={saveLabel}
          title={saveLabel}
          onClick={onSave}
          disabled={!isValid || busy}
          className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
        >
          <Check className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
